import React, { useMemo, useState } from 'react';
import {
  Button,
  Card,
  Divider,
  message,
  Space,
  Steps,
  Typography,
} from 'antd';

import ExtractedShopSelectStep from './components/ExtractedShopSelectStep';
import BasicInfoStep from './components/BasicInfoStep';
import LookbookStep from './components/LookbookStep';
import ReviewPhotoSelectStep from './components/ReviewPhotoSelectStep';
import ReviewItemSelectStep from './components/ReviewItemSelectStep';
import ReviewSummaryStep from './components/ReviewSummaryStep';

import { generateReviewSummaryPreview } from '@/api/partnerShops';
import { appendHeroImageFiles, stripHeroImageFiles } from './util';

const { Title, Text } = Typography;

export type PartnerShopStatus = 'DRAFT' | 'ACTIVE' | 'HIDDEN';

export type PartnerShopExtractedShopRelationType =
  | 'PRIMARY'
  | 'ALIAS'
  | 'BRANCH'
  | 'MERGED';

export interface JsonTitleDescriptionItem {
  title: string;
  description?: string;
  desc?: string;
  sortOrder?: number;
  isPublished?: number;
}

export interface PriceItem {
  title: string;
  description: string;
  priceText: string;
  includedItems?: string[];
  notice?: string;
  sortOrder: number;
  isPublished: number;
}

export interface FaqItem {
  question: string;
  answer: string;
  sortOrder?: number;
  isPublished?: number;
}

export interface PromotionBenefitItem {
  title: string;
  condition: string;
}

export interface PromotionBenefitGroup {
  vendorName: string;
  vendorCategory: string;
  benefits: PromotionBenefitItem[];
}

export interface HeroImageUploadItem {
  clientKey: string;
  sourceType: 'UPLOAD';

  file?: File;
  previewUrl?: string;
  originalFileName?: string;

  sortOrder: number;
}

export interface HeroImageGroup {
  main: HeroImageUploadItem | null;
  items: Array<HeroImageUploadItem | null>;
}
export interface VerifiedReasonsJson {
  introText: string;
  items: JsonTitleDescriptionItem[];
}
export interface HeroImagesJson {
  mobile: HeroImageGroup;
  desktop: HeroImageGroup;
}

/**
 * partnerShops
 */
export interface PartnerShop {
  shopName: string;
  displayName: string;
  shopSlug: string;
  normalizedShopName: string;
  shopCategory: string;
  branchName: string;
  areaName: string;

  shortDescription: string;
  brandStory: string;

  address: string;
  lat: number | null;
  lon: number | null;
  latitude: number | null;
  longitude: number | null;

  openingHours: string;
  parkingInfo: string;
  reservationMethod: string;
  consultationMethod: string;

  phone: string;
  homepageUrl: string;
  naverPlaceId?: string;
  naverMapUrl: string;
  googleMapUrl: string;

  badgesJson: JsonTitleDescriptionItem[];
  verifiedReasonsJson: VerifiedReasonsJson;
  featuresJson: JsonTitleDescriptionItem[];
  priceItemsJson: PriceItem[];
  faqJson: FaqItem[];
  noticeJson: JsonTitleDescriptionItem[];

  heroImagesJson: HeroImagesJson;
  promotionBenefitsJson: PromotionBenefitGroup[];

  isCertified: number;
  status: PartnerShopStatus;
  sortOrder: number;
}

/**
 * cafeExtractedShops
 * Step1 후보 표시용.
 * 저장 payload에는 들어가지 않는다.
 */
export interface CafeExtractedShop {
  id: number;
  shopName: string;
  normalizedShopName: string;
  shopCategory: string | null;

  articleCount: number;
  directReviewCount: number;
  indirectReviewCount: number;
  imageCount: number;

  mappedPartnerShopText?: string | null;
}

/**
 * partnerShopExtractedShopMaps
 */
export interface PartnerShopExtractedShopMap {
  partnerShopId?: number;
  cafeExtractedShopId: number;
  relationType: PartnerShopExtractedShopRelationType;
  delYn: number;
}

/**
 * partnerShopLookbookPhotos
 *
 * imageUrl / thumbnailUrl / imageUrlW720 / imageUrlW1080 없음.
 * 서버가 file을 받아 업로드하고 URL을 만든다.
 */
export interface PartnerShopLookbookPhoto {
  id?: number;
  partnerShopId?: number;
  lookbookSectionId?: number | null;

  clientKey: string;
  file?: File;
  previewUrl?: string;
  originalFileName?: string;

  category?: string;
  title?: string;
  description?: string;

  isRepresentative: number;
  isPublished: number;
  sortOrder: number;
  delYn: number;
}

/**
 * partnerShopLookbookSections
 */
export interface PartnerShopLookbookSection {
  id?: number;
  partnerShopId?: number;

  sectionName: string;
  description: string;
  isPublished: number;
  sortOrder: number;
  delYn: number;

  photos: PartnerShopLookbookPhoto[];
}

/**
 * partnerShopReviewPhotos
 *
 * imageUrl / thumbnailUrl / sourceUrl / confidence 없음.
 * cafeArticleImageShopId 기준으로 저장한다.
 */
export interface PartnerShopReviewPhoto {
  id?: number;
  partnerShopId?: number;

  cafeArticleImageId: number | null;
  cafeArticleImageShopId: number | null;
  shopImageCategoryId: number | null;

  caption: string;
  isPublished: number;
  sortOrder: number;
  delYn: number;
}

/**
 * 후기 사진 화면 표시용.
 * 저장 payload에는 들어가지 않는다.
 */
export interface CafeArticleImageShopView {
  cafeArticleImageShopId: number;
  cafeArticleImageId: number | null;

  imageUrl: string;
  imageUrlW300?: string;
  imageUrlW720?: string;
  imageUrlW1080?: string;

  categoryName: string;
  sourceUrl?: string;
  confidence?: number | null;
}

/**
 * partnerShopReviewItems
 */
export interface PartnerShopReviewItem {
  id?: number;
  partnerShopId?: number;

  cafeArticleAnalysisShopId: number;
  isPublished: number;
  sortOrder: number;
  delYn: number;
}

/**
 * 후기 카드 화면 표시용.
 * 저장 payload에는 들어가지 않는다.
 */
export interface CafeArticleAnalysisShopView {
  cafeArticleAnalysisShopId: number;

  cafeArticleId: number;
  cafeArticleAnalysisId?: number | null;
  cafeExtractedShopId?: number | null;

  title: string;
  reviewDate?: string | null;
  menuName?: string | null;
  originalUrl?: string | null;

  evaluationLevel?: string | null;
  sentiment?: string | null;
  confidence?: number | null;

  summary: string;
  contentPreview: string;
  goodPointsJson: JsonTitleDescriptionItem[];
  badPointsJson: JsonTitleDescriptionItem[];
  tagsJson: string[];
  thumbnailImagesJson: string[];
}

/**
 * partnerShopReviewSummaries
 */
export interface PartnerShopReviewSummary {
  id?: number;
  partnerShopId?: number;

  importedLlmDraftId: number | null;

  totalMentionCount: number;
  evaluatedReviewCount: number;
  directReviewCount: number;
  indirectReviewCount: number;
  mentionedOnlyCount: number;

  summaryText: string;
  plannerText: string;
  prosJson: JsonTitleDescriptionItem[];
  consJson: JsonTitleDescriptionItem[];
  keywordsJson: string[];
  sourceCafeArticleIdsJson: number[];

  status: 'READY' | 'NEED_REVIEW' | 'PUBLISHED';
  delYn: number;
}

const EMPTY_PARTNER_SHOP: PartnerShop = {
  shopName: '',
  displayName: '',
  shopSlug: '',
  normalizedShopName: '',
  shopCategory: '예복',
  branchName: '',
  areaName: '',

  shortDescription: '',
  brandStory: '',

  address: '',
  lat: null,
  lon: null,
  latitude: null,
  longitude: null,

  openingHours: '',
  parkingInfo: '',
  reservationMethod: '',
  consultationMethod: '',

  phone: '',
  homepageUrl: '',
  naverMapUrl: '',
  googleMapUrl: '',

  badgesJson: [],
  verifiedReasonsJson: {
    introText: '',
    items: [],
  },
  featuresJson: [],
  priceItemsJson: [],
  faqJson: [],
  noticeJson: [],

  heroImagesJson: {
    mobile: {
      main: null,
      items: [null, null, null, null],
    },
    desktop: {
      main: null,
      items: [null, null, null, null],
    },
  },

  promotionBenefitsJson: [],

  isCertified: 1,
  status: 'DRAFT',
  sortOrder: 0,
};

const EMPTY_PARTNER_SHOP_REVIEW_SUMMARY: PartnerShopReviewSummary = {
  importedLlmDraftId: null,

  totalMentionCount: 0,
  evaluatedReviewCount: 0,
  directReviewCount: 0,
  indirectReviewCount: 0,
  mentionedOnlyCount: 0,

  summaryText: '',
  plannerText: '',
  prosJson: [],
  consJson: [],
  keywordsJson: [],
  sourceCafeArticleIdsJson: [],

  status: 'READY',
  delYn: 0,
};

const steps = [
  { title: 'AI 업체 연결', description: '업체 후보 선택' },
  { title: '기본 정보', description: '업체 정보 입력' },
  { title: '룩북', description: '공식 사진 구성' },
  { title: '후기 사진', description: '노출 사진 선택' },
  { title: '후기 선택', description: '노출 후기 선택' },
  { title: '후기 요약', description: '요약 초안 선택/수정' },
];

function toPositiveNumber(value: unknown) {
  const numberValue = Number(value);

  if (Number.isNaN(numberValue) || numberValue < 1) {
    return null;
  }

  return numberValue;
}

function toNumber(value: unknown, fallback: number) {
  const numberValue = Number(value);

  if (Number.isNaN(numberValue)) {
    return fallback;
  }

  return numberValue;
}

function buildPartnerShopLookbookSectionsPayload(
  partnerShopLookbookSections: PartnerShopLookbookSection[]
) {
  return partnerShopLookbookSections.map((section, sectionIndex) => ({
    sectionName: section.sectionName || '',
    description: section.description || '',
    isPublished: toNumber(section.isPublished, 1),
    sortOrder: toNumber(section.sortOrder, sectionIndex + 1),
    delYn: toNumber(section.delYn, 0),

    photos: section.photos.map((photo, photoIndex) => ({
      clientKey: photo.clientKey,

      category: photo.category || section.sectionName || '기타',
      title: photo.title || '',
      description: photo.description || '',

      isRepresentative: toNumber(photo.isRepresentative, 0),
      isPublished: toNumber(photo.isPublished, 1),
      sortOrder: toNumber(photo.sortOrder, photoIndex + 1),
      delYn: toNumber(photo.delYn, 0),
    })),
  }));
}

function buildPartnerShopReviewPhotosPayload(
  partnerShopReviewPhotos: PartnerShopReviewPhoto[]
) {
  return partnerShopReviewPhotos
    .map((photo, index) => ({
      cafeArticleImageId: toPositiveNumber(photo.cafeArticleImageId),
      cafeArticleImageShopId: toPositiveNumber(photo.cafeArticleImageShopId),
      shopImageCategoryId: toPositiveNumber(photo.shopImageCategoryId),

      caption: photo.caption || '',
      isPublished: toNumber(photo.isPublished, 1),
      sortOrder: toNumber(photo.sortOrder, index + 1),
      delYn: toNumber(photo.delYn, 0),
    }))
    .filter(
      (photo) => photo.cafeArticleImageId && photo.cafeArticleImageShopId
    );
}

function buildPartnerShopReviewItemsPayload(
  partnerShopReviewItems: PartnerShopReviewItem[]
) {
  return partnerShopReviewItems
    .map((item, index) => ({
      cafeArticleAnalysisShopId: toPositiveNumber(
        item.cafeArticleAnalysisShopId
      ),
      isPublished: toNumber(item.isPublished, 1),
      sortOrder: toNumber(item.sortOrder, index + 1),
      delYn: toNumber(item.delYn, 0),
    }))
    .filter((item) => item.cafeArticleAnalysisShopId);
}


export function normalizeVerifiedReasonsJson(value: unknown, previous?: VerifiedReasonsJson): VerifiedReasonsJson {
  if (Array.isArray(value)) {
    return {
      introText: previous?.introText || '',
      items: value,
    };
  }

  if (value && typeof value === 'object') {
    const nextValue = value as Partial<VerifiedReasonsJson>;

    return {
      introText: nextValue.introText || previous?.introText || '',
      items: Array.isArray(nextValue.items) ? nextValue.items : [],
    };
  }

  return {
    introText: previous?.introText || '',
    items: previous?.items || [],
  };
}
export default function PartnerCreatePage() {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [saving, setSaving] = useState<boolean>(false);
  const [generatingReviewSummary, setGeneratingReviewSummary] =
    useState<boolean>(false);

  const [partnerShop, setPartnerShop] =
    useState<PartnerShop>(EMPTY_PARTNER_SHOP);

  /**
   * Step1 후보 표시용.
   * DB 저장 대상 아님.
   */
  const [cafeExtractedShops, setCafeExtractedShops] = useState<
    CafeExtractedShop[]
  >([]);

  /**
   * partnerShopExtractedShopMaps
   */
  const [
    partnerShopExtractedShopMaps,
    setPartnerShopExtractedShopMaps,
  ] = useState<PartnerShopExtractedShopMap[]>([]);

  /**
   * partnerShopLookbookSections + partnerShopLookbookPhotos
   */
  const [
    partnerShopLookbookSections,
    setPartnerShopLookbookSections,
  ] = useState<PartnerShopLookbookSection[]>([]);

  /**
   * partnerShopReviewPhotos
   */
  const [partnerShopReviewPhotos, setPartnerShopReviewPhotos] = useState<
    PartnerShopReviewPhoto[]
  >([]);

  /**
   * 후기 사진 화면 표시용.
   * DB 저장 대상 아님.
   */
  const [
    cafeArticleImageShopViews,
    setCafeArticleImageShopViews,
  ] = useState<Record<string, CafeArticleImageShopView>>({});

  /**
   * partnerShopReviewItems
   */
  const [partnerShopReviewItems, setPartnerShopReviewItems] = useState<
    PartnerShopReviewItem[]
  >([]);

  /**
   * 후기 카드 화면 표시용.
   * DB 저장 대상 아님.
   */
  const [
    cafeArticleAnalysisShopViews,
    setCafeArticleAnalysisShopViews,
  ] = useState<Record<string, CafeArticleAnalysisShopView>>({});

  /**
   * partnerShopReviewSummaries
   */
  const [partnerShopReviewSummary, setPartnerShopReviewSummary] =
    useState<PartnerShopReviewSummary>(EMPTY_PARTNER_SHOP_REVIEW_SUMMARY);

  const sourceCafeExtractedShopIds = useMemo(() => {
    return partnerShopExtractedShopMaps
      .map((item) => Number(item.cafeExtractedShopId))
      .filter((id) => !Number.isNaN(id) && id > 0);
  }, [partnerShopExtractedShopMaps]);

  const goNext = () => {
    if (
      currentStep === 0 &&
      partnerShopExtractedShopMaps.length === 0
    ) {
      message.warning('연결할 AI 추출 업체를 하나 이상 선택해 주세요.');
      return;
    }

    if (currentStep === 1) {
      if (!partnerShop.displayName.trim()) {
        message.warning('노출 업체명을 입력해 주세요.');
        return;
      }

      if (!partnerShop.shopSlug.trim()) {
        message.warning('업체 슬러그를 입력해 주세요.');
        return;
      }

      if (!partnerShop.shopCategory.trim()) {
        message.warning('업체 카테고리를 선택해 주세요.');
        return;
      }
    }

    setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const goPrev = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleGenerateReviewSummary = async () => {
    if (sourceCafeExtractedShopIds.length === 0) {
      message.warning('먼저 partnerShopExtractedShopMaps를 선택해 주세요.');
      return;
    }

    setGeneratingReviewSummary(true);

    try {
      const result = await generateReviewSummaryPreview({
        shopName: partnerShop.displayName || partnerShop.shopName,
        sourceShopIds: sourceCafeExtractedShopIds,
      });

      setPartnerShop((prev) => ({
        ...prev,
        verifiedReasonsJson: normalizeVerifiedReasonsJson(
          result.verifiedReasonsJson,
          prev.verifiedReasonsJson
        ),
      }));

      setPartnerShopReviewSummary((prev) => ({
        ...prev,
        ...result.partnerShopReviewSummary,
        ...result.reviewSummary,
      }));

      message.success(
        `후기 요약 초안을 생성했습니다. 사용된 후기 ${result.usedReviewCount || 0}개`
      );
    } catch (error) {
      console.error(
        '[PartnerCreatePage] failed to generate partnerShopReviewSummary',
        error
      );

      const errorMessage =
        error instanceof Error
          ? error.message
          : '후기 요약 초안 생성 중 오류가 발생했습니다.';

      message.error(errorMessage);
    } finally {
      setGeneratingReviewSummary(false);
    }
  };

  const handleCreate = async () => {
    setSaving(true);

    try {
      const formData = new FormData();

      const payload = {
        partnerShop: {
          ...partnerShop,
          shopName: partnerShop.displayName,
          heroImagesJson: stripHeroImageFiles(partnerShop.heroImagesJson),
        },

        partnerShopExtractedShopMaps: partnerShopExtractedShopMaps
          .map((item) => ({
            cafeExtractedShopId: toPositiveNumber(item.cafeExtractedShopId),
            relationType: item.relationType || 'ALIAS',
            delYn: toNumber(item.delYn, 0),
          }))
          .filter((item) => item.cafeExtractedShopId),

        partnerShopLookbookSections:
          buildPartnerShopLookbookSectionsPayload(
            partnerShopLookbookSections
          ),

        partnerShopReviewPhotos:
          buildPartnerShopReviewPhotosPayload(partnerShopReviewPhotos),

        partnerShopReviewItems:
          buildPartnerShopReviewItemsPayload(partnerShopReviewItems),

        partnerShopReviewSummary,
      };

      formData.append('payload', JSON.stringify(payload));

      appendHeroImageFiles(formData, partnerShop.heroImagesJson);

      partnerShopLookbookSections.forEach((section) => {
        section.photos.forEach((photo) => {
          if (photo.file && photo.clientKey) {
            formData.append(
              `partnerShopLookbookPhotos.${photo.clientKey}`,
              photo.file
            );
          }
        });
      });

      const response = await fetch('/api/admin/partner-shops', {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (!response.ok || result.status !== 200) {
        throw new Error(result.message || '업체 생성 중 오류가 발생했습니다.');
      }

      message.success('업체를 생성했습니다.');
    } catch (error) {
      console.error('[PartnerCreatePage] failed to create partnerShop', error);

      const errorMessage =
        error instanceof Error
          ? error.message
          : '업체 생성 중 오류가 발생했습니다.';

      message.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 1280, margin: '0 auto' }}>
      <Space direction="vertical" size={20} style={{ width: '100%' }}>
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>
            인증업체 추가
          </Title>
          <Text type="secondary">
            기본적인 인증업체 정보와 AI가 분석한 카페글을 이용해서 인증업체 정보를 등록합니다.
          </Text>
        </div>

        <Card>
          <Steps current={currentStep} items={steps} />
        </Card>

        {currentStep === 0 && (
          <ExtractedShopSelectStep
            cafeExtractedShops={cafeExtractedShops}
            onChangeCafeExtractedShops={setCafeExtractedShops}
            partnerShopExtractedShopMaps={partnerShopExtractedShopMaps}
            onChangePartnerShopExtractedShopMaps={
              setPartnerShopExtractedShopMaps
            }
          />
        )}

        {currentStep === 1 && (
          <BasicInfoStep
            partnerShop={partnerShop}
            onChangePartnerShop={setPartnerShop}
          />
        )}

        {currentStep === 2 && (
          <LookbookStep
            partnerShopLookbookSections={partnerShopLookbookSections}
            onChangePartnerShopLookbookSections={setPartnerShopLookbookSections}
          />
        )}

        {currentStep === 3 && (
          <ReviewPhotoSelectStep
            partnerShopExtractedShopMaps={partnerShopExtractedShopMaps}
            partnerShopReviewPhotos={partnerShopReviewPhotos}
            onChangePartnerShopReviewPhotos={setPartnerShopReviewPhotos}
            cafeArticleImageShopViews={cafeArticleImageShopViews}
            onChangeCafeArticleImageShopViews={setCafeArticleImageShopViews}
          />
        )}

        {currentStep === 4 && (
          <ReviewItemSelectStep
            partnerShopExtractedShopMaps={partnerShopExtractedShopMaps}
            partnerShopReviewItems={partnerShopReviewItems}
            onChangePartnerShopReviewItems={setPartnerShopReviewItems}
            cafeArticleAnalysisShopViews={cafeArticleAnalysisShopViews}
            onChangeCafeArticleAnalysisShopViews={setCafeArticleAnalysisShopViews}
            onGeneratePartnerShopReviewSummary={handleGenerateReviewSummary}
            generatingPartnerShopReviewSummary={generatingReviewSummary}
          />
        )}

       {currentStep === 5 && (
          <ReviewSummaryStep
            shopName={partnerShop.displayName || partnerShop.shopName}
            sourceCafeExtractedShopIds={sourceCafeExtractedShopIds}
            partnerShopReviewSummary={partnerShopReviewSummary}
            onChangePartnerShopReviewSummary={setPartnerShopReviewSummary}
            verifiedReasonsJson={partnerShop.verifiedReasonsJson}
            onChangeVerifiedReasonsJson={(verifiedReasonsJson) => {
              setPartnerShop((prev) => ({
                ...prev,
                verifiedReasonsJson,
              }));
            }}
          />
        )}

        <Divider />

        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button disabled={currentStep === 0} onClick={goPrev}>
            이전
          </Button>

          <Space>
            <Text type="secondary">
              선택된 업체 : {partnerShopExtractedShopMaps.length.toLocaleString()}개
            </Text>

            {currentStep < steps.length - 1 ? (
              <Button type="primary" onClick={goNext}>
                다음
              </Button>
            ) : (
              <Button type="primary" loading={saving} onClick={handleCreate}>
                업체 생성
              </Button>
            )}
          </Space>
        </div>
      </Space>
    </div>
  );
}
